import { useEffect } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Command } from 'cmdk'
import { Search } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export interface CommandEntry {
  /** Stable id — used as React key + cmdk value. */
  id: string
  label: React.ReactNode
  /** Optional metadata — shown right-aligned (shortcut, timestamp). */
  meta?: React.ReactNode
  /** Keywords included in fuzzy-match. */
  keywords?: string[]
  /** Logical group heading. Default: "Modules". */
  group?: string
  /** Leading icon. */
  icon?: React.ComponentType<{ size?: number; className?: string }>
  /** Invoked on select. */
  onSelect: () => void
}

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entries: CommandEntry[]
  placeholder?: string
  emptyMessage?: string
  /** Footer hint text — defaults to the keyboard legend. */
  footerHint?: React.ReactNode
}

/**
 * Global command palette. `⌘K` opens from anywhere — the shell wires
 * that shortcut at the app root; this component only renders.
 *
 * Spec §11 (nav-only for v1). Groups: Modules, Recent, Actions.
 * Arrow keys navigate, Enter selects, Esc closes.
 */
export function CommandPalette({
  open,
  onOpenChange,
  entries,
  placeholder = 'Search modules, records, actions…',
  emptyMessage = 'No results',
  footerHint,
}: CommandPaletteProps) {
  useEffect(() => {
    // cmdk handles arrow / enter internally; we only need the global ⌘K
    // opener. Close on Esc is handled by Radix Dialog.
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  const groups = groupEntries(entries)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-overlay bg-bg-overlay animate-in fade-in-0 duration-fast" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-20 z-modal w-[640px] max-w-[calc(100vw-2rem)] -translate-x-1/2',
            'overflow-hidden rounded-lg border border-border-default bg-surface shadow-xl',
            'animate-in fade-in-0 zoom-in-95 duration-fast',
          )}
        >
          <VisuallyHidden>
            <DialogPrimitive.Title>Command palette</DialogPrimitive.Title>
          </VisuallyHidden>
          <Command label="Command palette">
            <div className="flex h-11 items-center gap-2 border-b border-border-subtle px-3">
              <Search size={14} className="text-tertiary" />
              <Command.Input
                placeholder={placeholder}
                className={cn(
                  'w-full border-none bg-transparent text-md text-primary outline-none',
                  'placeholder:text-tertiary',
                )}
              />
              <span
                className="text-role-kbd-shortcut inline-flex items-center rounded-xs px-1 text-tertiary"
                style={{ background: 'var(--color-kbd-bg)' }}
              >
                Esc
              </span>
            </div>
            <Command.List className="max-h-80 overflow-y-auto py-1">
              <Command.Empty className="px-3 py-6 text-center text-sm text-tertiary">
                {emptyMessage}
              </Command.Empty>
              {groups.map((group) => (
                <Command.Group
                  key={group.label ?? '_'}
                  heading={group.label}
                  className={cn(
                    '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1',
                    '[&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-semibold',
                    '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-tertiary',
                  )}
                >
                  {group.items.map((item) => {
                    const Icon = item.icon
                    return (
                      <Command.Item
                        key={item.id}
                        value={`${item.group ?? 'Modules'}/${item.id}`}
                        keywords={item.keywords}
                        onSelect={() => {
                          item.onSelect()
                          onOpenChange(false)
                        }}
                        className={cn(
                          'grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-2 px-3 py-1.5 text-sm',
                          'data-[selected=true]:bg-accent-subtle data-[selected=true]:text-accent-text',
                        )}
                      >
                        <span className="flex h-4 w-4 items-center justify-center text-tertiary">
                          {Icon ? <Icon size={14} /> : null}
                        </span>
                        <span className="truncate">{item.label}</span>
                        {item.meta ? (
                          <span className="text-role-metadata">{item.meta}</span>
                        ) : null}
                      </Command.Item>
                    )
                  })}
                </Command.Group>
              ))}
            </Command.List>
            <div className="text-role-metadata flex items-center justify-between border-t border-border-subtle bg-surface-sunk px-3 py-1.5">
              {footerHint ?? (
                <>
                  <span>
                    <Kbd>↑</Kbd>
                    <Kbd>↓</Kbd> navigate · <Kbd>↵</Kbd> select
                  </span>
                  <span>
                    <Kbd>⌘K</Kbd> to toggle
                  </span>
                </>
              )}
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="mx-0.5 inline-flex h-4 items-center rounded-xs px-1 text-[10px]"
      style={{ background: 'var(--color-kbd-bg)' }}
    >
      {children}
    </span>
  )
}

function groupEntries(entries: CommandEntry[]): { label: string | undefined; items: CommandEntry[] }[] {
  const map = new Map<string | undefined, CommandEntry[]>()
  for (const entry of entries) {
    const key = entry.group
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(entry)
  }
  return Array.from(map, ([label, items]) => ({ label, items }))
}
